import React, { useState } from 'react';
import type { LoginCredentials } from '@/types/auth.types';
import { Button } from '@/components/common';
import { 
  FaUser, 
  FaLock, 
  FaSignInAlt, 
  FaSpinner, 
  FaExclamationTriangle 
} from 'react-icons/fa';

interface LoginFormProps {
  onSubmit: (credentials: LoginCredentials) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  /** Shows a "Forgot password?" link under the password field when given. */
  onForgotPassword?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSubmit, isLoading, error, onForgotPassword }) => {
  const [formData, setFormData] = useState<LoginCredentials>({
    username: '',
    password: ''
  });
  const [fieldErrors, setFieldErrors] = useState<Partial<LoginCredentials>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name as keyof LoginCredentials]) {
      setFieldErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateUsername = (username: string): string | undefined => {
    if (!username) return 'Username is required';
    if (username.length < 3) return 'Username must be at least 3 characters';
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) return 'Password is required';
    return undefined;
  };

  const validateForm = (): boolean => {
    const errors: Partial<LoginCredentials> = {};
    
    errors.username = validateUsername(formData.username);
    errors.password = validatePassword(formData.password);
    
    setFieldErrors(errors);
    return !Object.values(errors).some(error => error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || isLoading) return;
    
    await onSubmit(formData);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    let error: string | undefined;
    if (name === 'username') {
      error = validateUsername(value);
    } else if (name === 'password') {
      error = validatePassword(value);
    }
    
    setFieldErrors(prev => ({ ...prev, [name]: error }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Error Message */}
      {error && (
        <div className="p-4 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg">
          <div className="flex items-center gap-3">
            <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}
      
      {/* Username Field */}
      <div>
        <label htmlFor="username" className="block text-sm font-semibold text-white mb-2">
          <div className="flex items-center gap-2">
            <FaUser className="text-game-golden" />
            Username
          </div>
        </label>
        <input
          type="text"
          id="username"
          name="username"
          value={formData.username}
          onChange={handleInputChange}
          onBlur={handleBlur}
          required
          autoComplete="username"
          className={`w-full px-4 py-3 bg-game-cream border border-game-medium/50 rounded-lg text-gray-900 placeholder-gray-600 focus:border-game-golden focus:bg-game-cream/90 focus:shadow-lg focus:shadow-game-golden/20 focus:outline-none transition-all duration-200 ${
            fieldErrors.username ? 'border-game-orange bg-game-orange/20' : ''
          }`}
          placeholder="Enter your username"
        />
        {fieldErrors.username && (
          <div className="mt-2 p-2 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-md text-sm flex items-center gap-2">
            <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
            {fieldErrors.username}
          </div>
        )}
      </div>

      {/* Password Field */}
      <div>
        <label htmlFor="password" className="block text-sm font-semibold text-white mb-2">
          <div className="flex items-center gap-2">
            <FaLock className="text-game-golden" />
            Password
          </div>
        </label>
        <input
          type="password"
          id="password"
          name="password"
          value={formData.password}
          onChange={handleInputChange}
          onBlur={handleBlur}
          required
          autoComplete="current-password"
          className={`w-full px-4 py-3 bg-game-cream border border-game-medium/50 rounded-lg text-gray-900 placeholder-gray-600 focus:border-game-golden focus:bg-game-cream/90 focus:shadow-lg focus:shadow-game-golden/20 focus:outline-none transition-all duration-200 ${
            fieldErrors.password ? 'border-game-orange bg-game-orange/20' : ''
          }`}
          placeholder="Enter your password"
        />
        {fieldErrors.password && (
          <div className="mt-2 p-2 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-md text-sm flex items-center gap-2">
            <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
            {fieldErrors.password}
          </div>
        )}
        {onForgotPassword && (
          <div className="mt-2 text-right">
            <button
              type="button"
              onClick={onForgotPassword}
              className="text-sm text-game-golden hover:text-game-accent underline-offset-2 hover:underline"
            >
              Forgot password?
            </button>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <Button 
        type="submit" 
        variant="primary"
        isLoading={isLoading}
        className="w-full px-6 py-3"
      >
        <FaSignInAlt className="text-lg" />
        <span>Sign In</span>
      </Button>
    </form>
  );
};